import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  type FieldStatusDto,
  type SourcingEventDto,
} from "../../../../../../autogen/bff-api";
import { useFieldErrorTranslation } from "../../../../../../common/editing/save-coordinator";
import type { FieldError } from "../../../../../../common/editing/save-coordinator/types";
import type { ReadinessLookup, KnownFieldKey } from "../../hooks/useReadinessByField";
import type { PublicEventStep } from "../../../../../../common/sourcing-events/buyer/wizardSteps";
import { hasRequestStage, isQualificationScheme, isTwoStageProcedure } from "../../utils/procedure";
import { MilestoneTimeline } from "../MilestoneTimeline";
import { ReadinessBadge } from "./ReadinessBadge";
import { FieldSourceMark } from "./FieldSourceMark";
import { SummaryFieldLabel } from "./SummaryField";
import { tenderValidityShown } from "./tenderValidityShown";
import { SummarySection } from "./SummarySection";

const MILESTONE_PLAN_PATHS = ["eventData.milestonePlan"];

export const FIELD_KEYS = [
  "BidSubmissionDeadlinePresent",
  "BidSubmissionDeadlineInFuture",
  "BidSubmissionDeadlineAboveMinimum",
  "BidSubmissionDeadlineConfirmed",
  "QuestionDeadlineBetweenAnnouncementAndBidDeadline",
  "RequestDeadlinePresent",
  "RequestDeadlineAboveMinimum",
  "ShortRequestDeadlineJustified",
  "RequestDeadlineConfirmed",
  "AcceleratedProcedureJustified",
] as const satisfies readonly KnownFieldKey[];

const worseReadiness = (
  ...statuses: (FieldStatusDto | undefined)[]
): FieldStatusDto | undefined =>
  statuses.find((s) => s && !s.satisfied) ?? statuses.find((s) => s !== undefined);

interface Props {
  event: SourcingEventDto;
  savedBidDeadline?: { value: string | null | undefined; error: FieldError };
  savedRequestDeadline?: {
    value: string | null | undefined;
    confirmed: boolean | null | undefined;
    error: FieldError;
  };
  readinessFor: ReadinessLookup;
  onWizardStepNavigate: (step: PublicEventStep) => void;
}

const formatOsloDateTime = (iso: string): string =>
  `${new Date(iso).toLocaleDateString("nb-NO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "Europe/Oslo",
  })} ${new Date(iso).toLocaleTimeString("nb-NO", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Oslo",
  })}`;

export const DeadlinesAndMilestonesSection = ({
  event: editedEvent,
  savedBidDeadline,
  savedRequestDeadline,
  readinessFor,
  onWizardStepNavigate,
}: Props) => {
  const { t } = useTranslation();
  const translateFieldError = useFieldErrorTranslation();
  const event = {
    ...editedEvent,
    eventData: {
      ...editedEvent.eventData,
      ...(savedBidDeadline && { bidSubmissionDeadline: savedBidDeadline.value }),
      ...(savedRequestDeadline && {
        requestDeadline: savedRequestDeadline.value,
        requestDeadlineConfirmed: savedRequestDeadline.confirmed,
      }),
    },
  };
  const { eventData } = event;
  const publicContext = event.eventContext.type === "Public" ? event.eventContext : null;
  const deadline = eventData.bidSubmissionDeadline ?? null;
  const validity = tenderValidityShown(event);
  const tenderValidity = publicContext?.qualificationSchemeCompetition
    ? t("Does not apply to a competition under a qualification system")
    : validity.notStatedInInvitation
      ? t("Not stated in the invitation")
      : validity.months != null
        ? t("{{count}} months", { count: validity.months })
        : null;
  const twoStage = hasRequestStage(publicContext);

  const fieldStatuses = FIELD_KEYS.map(readinessFor).filter(
    (s): s is FieldStatusDto => s !== undefined,
  );

  return (
    <SummarySection
      title={t("Deadlines and milestones")}
      editTarget={{ kind: "wizard-step", step: "procedure" }}
      fieldStatuses={fieldStatuses}
      onWizardStepNavigate={onWizardStepNavigate}
    >
      {publicContext?.acceleratedProcedure === true && (
        <Flex gap="2" align="center" mb="4">
          <Text fontSize="sm" color="smText">
            {t("Urgent case")}: <b>{publicContext.acceleratedJustification?.trim() || t("Not justified")}</b>
          </Text>
          <ReadinessBadge status={readinessFor("AcceleratedProcedureJustified")} />
        </Flex>
      )}
      {twoStage && (
        <Box mb="4">
          <Flex gap="2" align="center">
            <Text fontSize="sm" color="smText">
              {t("Deadline for requests to participate")}:{" "}
              <b>
                {eventData.requestDeadline
                  ? formatOsloDateTime(eventData.requestDeadline)
                  : t("Not set")}
              </b>
            </Text>
            <FieldSourceMark paths={["eventData.requestDeadline"]} fieldLabel={t("Deadline for requests to participate")} />
            <ReadinessBadge
              status={worseReadiness(
                readinessFor("RequestDeadlinePresent"),
                readinessFor("RequestDeadlineAboveMinimum"),
                readinessFor("ShortRequestDeadlineJustified"),
                readinessFor("RequestDeadlineConfirmed"),
              )}
            />
          </Flex>
          {savedRequestDeadline && (
            <Text fontSize="sm" fontWeight="normal" color="smMuted" mt="1">
              {t(
                "Your latest change to the deadline for requests to participate was not saved. This is the deadline the procurement has.",
              )}{" "}
              {translateFieldError(savedRequestDeadline.error)}{" "}
              <Button variant="link" color="smPrimary" size="sm" onClick={() => onWizardStepNavigate("procedure")}>
                {t("Change the deadline for requests to participate in «Procedure»")}
              </Button>
            </Text>
          )}
        </Box>
      )}
      <SummaryFieldLabel>
        {t("Milestone plan")}
        <FieldSourceMark paths={MILESTONE_PLAN_PATHS} fieldLabel={t("Milestone plan")} />
      </SummaryFieldLabel>
      <MilestoneTimeline
        mode="summary"
        event={event}
        deadlineBadge={
          <>
            <ReadinessBadge
              status={worseReadiness(
                readinessFor("BidSubmissionDeadlinePresent"),
                readinessFor("BidSubmissionDeadlineInFuture"),
                readinessFor("BidSubmissionDeadlineAboveMinimum"),
                readinessFor("BidSubmissionDeadlineConfirmed"),
                readinessFor("QuestionDeadlineBetweenAnnouncementAndBidDeadline"),
              )}
            />
            <FieldSourceMark paths={["eventData.bidSubmissionDeadline"]} fieldLabel={t("Bid deadline")} />
          </>
        }
      />
      {savedBidDeadline && (
        <Text fontSize="sm" fontWeight="normal" color="smMuted" mt="1">
          {t("Your latest change to the bid deadline was not saved. This is the deadline the procurement has.")}{" "}
          {translateFieldError(savedBidDeadline.error)}{" "}
          <Button variant="link" color="smPrimary" size="sm" onClick={() => onWizardStepNavigate("procedure")}>
            {t("Change the bid deadline in «Procedure»")}
          </Button>
        </Text>
      )}

      {deadline && tenderValidity && (
        <Flex gap="4" mt="5" wrap="wrap">
          <Text fontSize="xs" color="smMuted">
            {t("Tender validity")}: <b>{tenderValidity}</b>
          </Text>
        </Flex>
      )}
    </SummarySection>
  );
};
